import React from "react";
import { MbscCalendarEventData } from "@mobiscroll/react";
import { Grid2, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Cancel, DoNotDisturb, HourglassEmpty } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";
import { TeamMember } from "../../features/Booking-Form/typings/team-member";
import { ScheduleEventCheckbox } from "./schedule-event-checkbox.component";
import { ScheduleEventContentIcon } from "./schedule-event-content-icon.component";
import { hexToRGB } from "../FacilityManager/Functions/hexColorToRGB";
import { calBrightness } from "../FacilityManager/Functions/calculateBrightness";
import { BookingRequestsStatus } from "../BookingRequest/typings/booking-requests.types";
import { handleFirstAidUserName } from "../../features/Schedule/schedule.functions";
import { ScheduleMode } from "../../features/Schedule/partial/ScheduleModeToggle.partial";
import {
  resolveScheduleExternalMemberCalendarLabel,
  resolveScheduleVisitorCalendarLabel
} from "./functions/schedule-visitor-display-name.function";

type P = {
  event: MbscCalendarEventData;
  monthView?: boolean;
  schedule: BookingScheduleInterface;
  selectedColleagues?: TeamMember[];
  users?: TeamMember[];
  scheduleMode: ScheduleMode;
  selectedAppointments?: number[];
  setSelectedAppointment?: (selectedAppointment: number) => void;
};

export const ScheduleEventContent: React.FC<P> = ({
  event,
  monthView,
  schedule,
  selectedColleagues,
  users,
  scheduleMode,
  selectedAppointments,
  setSelectedAppointment
}) => {
  const { t } = useTranslation();
  const { userInformation } = useSelector((state: RootState) => state.login);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isColleagueMode = scheduleMode === ScheduleMode.Colleague;
  const displayCheckbox =
    isColleagueMode &&
    DateTime.fromISO(schedule.startDate).valueOf() > DateTime.now().valueOf() &&
    (selectedColleagues
      ? selectedColleagues
          .map((colleague: { userId: string }) => colleague.userId)
          .includes(userInformation.sub) && schedule.userBookedFor === userInformation.sub
      : true);

  const bookedForUser = users?.find(user => user.userId === schedule.userBookedFor);
  const multipleUsersSelected = users && users.length >= 2;

  const bookedForUserName = (() => {
    const isFirstAid = handleFirstAidUserName(bookedForUser?.firstName);
    if (isFirstAid) return isFirstAid;
    else return `${bookedForUser?.firstName} ${bookedForUser?.surname}`.trim();
  })();

  const visitorCalendarLabel = resolveScheduleVisitorCalendarLabel(schedule);
  const showVisitorLabel = visitorCalendarLabel !== null;
  const externalMemberCalendarLabel = resolveScheduleExternalMemberCalendarLabel(schedule);
  const showExternalMemberLabel = externalMemberCalendarLabel !== null && !showVisitorLabel;
  const showUserName =
    multipleUsersSelected && bookedForUser && !showVisitorLabel && !showExternalMemberLabel;
  const placeName =
    schedule.locationName === "Mobile Working"
      ? t("Mobile Working")
      : schedule.bookingInventory.name;

  const calendarLabelText = (() => {
    if (showVisitorLabel) {
      return visitorCalendarLabel;
    }
    if (showExternalMemberLabel) {
      return externalMemberCalendarLabel;
    }
    if (showUserName) {
      return bookedForUserName;
    }
    const visitOnlySuffix = schedule.isVisitOnly ? ` · ${t("_schedule_visitOnlyBadge")}` : "";
    return `${placeName}${visitOnlySuffix}`;
  })();

  const showLocationInWeekView = showUserName || showVisitorLabel || showExternalMemberLabel;

  return (
    <Grid2 container data-testid="calendar-item" direction="column">
      <Grid2 container wrap="nowrap" alignItems="center">
        <Grid2
          size={{ xs: displayCheckbox ? 10 : 12 }}
          sx={{
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            position: "relative",
            minWidth: 0
          }}
        >
          {!isMobile && isColleagueMode && (
            <InitStatusIcon
              status={schedule.status}
              color={calBrightness(hexToRGB(event.original?.color))}
            />
          )}

          <ScheduleEventContentIcon
            schedule={schedule}
            userId={userInformation.sub}
            textColor={calBrightness(hexToRGB(event.original?.color))}
            showConferenceVisitorContactLink={isColleagueMode}
          />

          <Typography
            variant={"inherit"}
            component="span"
            noWrap
            sx={[
              {
                paddingLeft: 0.2,
                color: calBrightness(hexToRGB(event.original?.color))
              },
              schedule.status === BookingRequestsStatus.REJECTED
                ? { textDecoration: "line-through" }
                : { textDecoration: "" }
            ]}
          >
            {calendarLabelText}
          </Typography>
        </Grid2>

        {displayCheckbox && (
          <Grid2
            size={{ xs: 2 }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexShrink: 0
            }}
          >
            <ScheduleEventCheckbox
              schedule={schedule}
              selectedAppointments={selectedAppointments}
              setSelectedAppointment={setSelectedAppointment}
            />
          </Grid2>
        )}
      </Grid2>

      {!monthView && (
        <Grid2 container data-testid="calendar-item-week-view" sx={{ flexDirection: "column" }}>
          {showLocationInWeekView && (
            <Grid2>
              <Typography
                variant={"inherit"}
                sx={{ color: calBrightness(hexToRGB(event.original?.color)) }}
              >
                {event.title}
                {schedule.isVisitOnly && !visitorCalendarLabel
                  ? ` · ${t("_schedule_visitOnlyBadge")}`
                  : ""}
              </Typography>
            </Grid2>
          )}

          <Grid2>
            <Typography
              variant={"inherit"}
              sx={{ color: calBrightness(hexToRGB(event.original?.color)) }}
            >
              {event.start} - {event.end}
            </Typography>
          </Grid2>
        </Grid2>
      )}
    </Grid2>
  );
};

type InitStatusIconProps = {
  status: BookingRequestsStatus | undefined;
  color?: "black" | "white";
};
export function InitStatusIcon({ status, color }: Readonly<InitStatusIconProps>) {
  switch (status) {
    case BookingRequestsStatus.APPROVED:
      return "";
    case BookingRequestsStatus.CANCELLED:
      return <Cancel sx={{ width: 18, height: 18, color: color }} />;
    case BookingRequestsStatus.REJECTED:
      return <DoNotDisturb sx={{ width: 18, height: 18, color: color }} />;
    case BookingRequestsStatus.REQUESTED:
      return <HourglassEmpty sx={{ width: 18, height: 18, color: color }} />;
    default:
      return "";
  }
}
